/**
 * Engine Chat Service
 * Hybrid service that uses REST API for conversation management
 * and WebSocket for real-time messaging with Bili engine
 */

import logger from '../utils/logger';

import engineApi from './engineApi';
import websocketService from './websocketService';

const ENGINE_API_BASE = '/conversations'; // Relative to engine base URL (which includes /api)

/**
 * Load all conversations from engine
 * @param {boolean} throwOnError - If true, throw errors instead of returning empty array
 * @returns {Promise<Array>} Array of conversation objects
 */
export const loadConversationsFromEngine = async (throwOnError = false) => {
    try {
        const conversations = await engineApi.get(ENGINE_API_BASE);

        // Transform to match chat UI format
        return conversations.map((conv) => {
            // Try multiple possible field names for title (API might use different naming)
            const title =
                conv.title ||
                conv.conversation_title ||
                conv.name ||
                (conv.conversation_id === 'default'
                    ? 'Default Conversation'
                    : `Chat ${conv.conversation_id.substring(0, 8)}`);

            // Try multiple possible field names for tags
            const tags = conv.tags || conv.labels || conv.topics || [];

            // Try multiple possible field names for category
            const category = conv.category || conv.type || 'general';

            // Parse last_updated timestamp
            let lastActivity;
            try {
                lastActivity = new Date(conv.last_updated);
                if (isNaN(lastActivity.getTime())) {
                    lastActivity = new Date();
                }
            } catch {
                lastActivity = new Date();
            }

            return {
                id: conv.thread_id,
                title,
                lastActivity,
                messageCount: conv.message_count || 0,
                lastMessage: conv.last_message || '',
                category,
                tags: Array.isArray(tags) ? tags : [],
                threadId: conv.thread_id,
                conversationId: conv.conversation_id,
            };
        });
    } catch (error) {
        logger.error('Failed to load conversations from engine:', error);
        if (throwOnError) {
            throw error;
        }
        return [];
    }
};

/**
 * Get conversation history from engine
 * @param {string} threadId - Thread ID to retrieve
 * @returns {Promise<Object>} Conversation object with messages
 */
export const getConversationFromEngine = async (threadId) => {
    try {
        const conversation = await engineApi.get(
            `${ENGINE_API_BASE}/${threadId}`
        );

        // Transform messages to match chat UI format
        const messages = (conversation.messages || []).map((msg, index) => {
            // Handle different message format possibilities
            const content = msg.content || msg.text || msg.message || '';
            const role = msg.role || msg.sender || 'unknown';

            return {
                id: msg.id || `${threadId}-${index}`,
                text: content,
                sender: role === 'user' ? 'user' : 'bot',
                timestamp:
                    msg.timestamp ||
                    msg.created_at ||
                    conversation.last_updated,
                // Map payload for ChatMapPanel. Placeholder field name -- the
                // engine does not send this yet; the map ticket owns the real
                // shape. This transform previously dropped every field it did
                // not name, so the pass-through is what makes the seam exist.
                mapVisualization:
                    msg.map_visualization || msg.mapVisualization || null,
            };
        });

        // Try multiple possible field names for title
        const title =
            conversation.title ||
            conversation.conversation_title ||
            conversation.name ||
            `Chat ${conversation.thread_id?.split('_')[1]?.substring(0, 8) || conversation.conversation_id?.substring(0, 8) || 'Default'}`;

        // Try multiple possible field names for tags
        const tags =
            conversation.tags ||
            conversation.labels ||
            conversation.topics ||
            [];

        // Try multiple possible field names for category
        const category =
            conversation.category || conversation.type || 'general';

        // Parse last_updated - use current time as fallback for invalid values
        let lastActivity;
        try {
            lastActivity = new Date(conversation.last_updated);
            if (isNaN(lastActivity.getTime())) {
                lastActivity = new Date();
            }
        } catch {
            lastActivity = new Date();
        }

        return {
            id: threadId,
            title,
            tags: Array.isArray(tags) ? tags : [],
            messages,
            lastActivity,
            metadata: {
                category,
                messageCount: messages.length, // Use actual message count, not the one from API
            },
        };
    } catch (error) {
        logger.error('Failed to get conversation from engine:', error);
        throw error;
    }
};

/**
 * Delete a conversation from engine
 * @param {string} threadId - Thread ID to delete
 * @returns {Promise<void>}
 */
export const deleteConversationFromEngine = async (threadId) => {
    try {
        await engineApi.delete(`${ENGINE_API_BASE}/${threadId}`);
    } catch (error) {
        logger.error('Failed to delete conversation from engine:', error);
        throw error;
    }
};

/**
 * Get usage statistics from engine
 * @returns {Promise<Object>} Usage stats
 */
export const getEngineStats = async () => {
    try {
        const stats = await engineApi.get(`${ENGINE_API_BASE}/stats`);
        return {
            totalMessages: stats.total_messages,
            dailyMessages: stats.daily_messages,
            dailyLimit: stats.daily_limit, // Role-based limit from API
            totalChats: stats.total_conversations,
        };
    } catch (error) {
        logger.error('Failed to get engine stats:', error);
        return {
            totalMessages: 0,
            dailyMessages: 0,
            dailyLimit: 100, // Default to most restrictive limit
            totalChats: 0,
        };
    }
};

/**
 * Connect to WebSocket for real-time messaging
 * @param {string} threadId - Optional thread ID to connect to
 * @param {string} persona - Optional persona route segment (e.g. "expert")
 * @returns {Promise<void>}
 */
export const connectToEngine = async (threadId = null, persona = null) => {
    await websocketService.connect(threadId, persona);
};

/**
 * Wait until the WebSocket is actually open before issuing a command.
 * @param {number} [timeout] - Max time to wait for the open event (ms)
 * @returns {Promise<void>}
 */
export const waitForEngineReady = (timeout) => {
    return websocketService.whenReady(timeout);
};

/**
 * Disconnect from WebSocket
 */
export const disconnectFromEngine = () => {
    websocketService.disconnect();
};

/**
 * Send a message via WebSocket and get response
 * @param {string} message - User message text
 * @param {Object} options - Optional extra data (e.g., { page_context })
 * @returns {Promise<Object>} Bot response
 */
export const sendMessageToEngine = async (message, options = {}) => {
    try {
        const response = await websocketService.sendMessage(message, options);

        // Transform response to match chat UI format
        return {
            id: `msg-${Date.now()}`,
            text:
                response.data?.response ||
                response.data?.data?.response ||
                'No response',
            sender: 'bot',
            timestamp: new Date(response.metadata?.time || Date.now()),
            thinking: response.data?.thinking, // Optional thinking process
            map: response.data?.map, // Map payload from the build_map_payload node (ticket 9)
            title: response.metadata?.title, // Conversation title from LLM
            tags: response.metadata?.tags || [], // Topic tags from LLM
            // Map payload for ChatMapPanel (placeholder field name, see above)
            mapVisualization: response.data?.map_visualization || null,
        };
    } catch (error) {
        logger.error('Failed to send message to engine:', error);
        throw error;
    }
};

/**
 * Create a new conversation via WebSocket
 * @returns {Promise<Object>} New conversation data
 */
export const createNewEngineConversation = async () => {
    try {
        const result = await websocketService.createNewConversation();
        return {
            threadId: result.thread_id,
            conversationId: result.conversation_id,
        };
    } catch (error) {
        logger.error('Failed to create new conversation:', error);
        throw error;
    }
};

/**
 * Switch to a different conversation
 * @param {string} threadId - Thread ID to switch to
 * @returns {Promise<void>}
 */
export const switchEngineConversation = async (threadId) => {
    await websocketService.switchConversation(threadId);
};

/**
 * Register a message handler for incoming WebSocket messages
 * @param {Function} handler - Handler function
 */
export const onEngineMessage = (handler) => {
    websocketService.on('message', handler);
};

/**
 * Unregister a message handler
 * @param {Function} handler - Handler function to remove
 */
export const offEngineMessage = (handler) => {
    websocketService.off('message', handler);
};

/**
 * Check if connected to WebSocket
 * @returns {boolean}
 */
export const isConnectedToEngine = () => {
    return websocketService.isConnected();
};

/**
 * Get current thread ID
 * @returns {string|null}
 */
export const getCurrentThreadId = () => {
    return websocketService.getThreadId();
};

/**
 * Get the last error type from WebSocket connection
 * @returns {string|null} Error type or null
 */
export const getEngineErrorType = () => {
    return websocketService.getLastErrorType();
};

/**
 * Register a close handler for WebSocket disconnection events
 * @param {Function} handler - Handler function
 */
export const onEngineClose = (handler) => {
    websocketService.on('close', handler);
};

/**
 * Unregister a close handler
 * @param {Function} handler - Handler function to remove
 */
export const offEngineClose = (handler) => {
    websocketService.off('close', handler);
};

// Re-export error types for use in components
export { WS_ERROR_TYPES } from './websocketService';
